import React from "react";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./pagination";

type PaginationSelectorProps = {
  disabled?: boolean;
  total: number;
  currentPage: number;
  setPage: (page: number) => void;
  maxDisplayed: number;
  limit: number;
};

const PaginationSelector: React.FC<PaginationSelectorProps> = ({
  currentPage,
  setPage,
  maxDisplayed,
  limit,
  total,
  disabled = false,
}) => {
  const totalPages = Math.ceil(total / limit);

  const getPageNumbers = () => {
    const halfMax = Math.floor(maxDisplayed / 2);
    let startPage = Math.max(currentPage - halfMax, 1);
    const endPage = Math.min(startPage + maxDisplayed - 1, totalPages);

    if (endPage - startPage + 1 < maxDisplayed) {
      startPage = Math.max(endPage - maxDisplayed + 1, 1);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const pageNumbers = getPageNumbers();

  const onPrevious = () => setPage(Math.max(1, currentPage - 1));
  const onNext = () => setPage(Math.min(totalPages, currentPage + 1));
  const firstPageNumber = pageNumbers[0] ?? 0;
  const lastPageNumber = pageNumbers[pageNumbers.length - 1] ?? 0;

  return (
    <Pagination>
      <PaginationContent>
        {currentPage !== 1 && (
          <PaginationItem
            role="button"
            onClick={() => {
              if (disabled) return;
              onPrevious();
            }}
          >
            <PaginationPrevious />
          </PaginationItem>
        )}

        {firstPageNumber > 1 && (
          <>
            <PaginationItem
              role="button"
              onClick={() => {
                if (disabled) return;
                setPage(1);
              }}
            >
              <PaginationLink>1</PaginationLink>
            </PaginationItem>
            {firstPageNumber > 2 && <PaginationEllipsis />}
          </>
        )}

        {pageNumbers.map((page) => (
          <PaginationItem
            key={`paginationItem${page}`}
            role="button"
            onClick={() => {
              if (disabled) return;
              setPage(page);
            }}
          >
            <PaginationLink isActive={page === currentPage}>
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {lastPageNumber < totalPages && (
          <>
            {lastPageNumber < totalPages - 1 && <PaginationEllipsis />}
            <PaginationItem
              role="button"
              onClick={() => {
                if (disabled) return;
                setPage(totalPages);
              }}
            >
              <PaginationLink>{totalPages}</PaginationLink>
            </PaginationItem>
          </>
        )}

        {currentPage !== totalPages && (
          <PaginationItem
            role="button"
            onClick={() => {
              if (disabled) return;
              onNext();
            }}
          >
            <PaginationNext />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
};

export { PaginationSelector };
